import { Text } from "@hackthenorth/north";
import React from "react";
import styled from "styled-components";

import { PointsIcon } from "src/static/icons";
import { EmptyImage } from "src/static/img";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { BalanceChip } from "src/views/goose-games/components/balanceChip";
import { StoreItem } from "src/views/goose-games/types";

interface Props {
  item: StoreItem;
  currentPoints: number;
}

export type PrizeStatus = "sold-out" | "redeemable" | "insufficient-funds";

export const STATUS_LABELS: Record<PrizeStatus, string> = {
  "sold-out": "SOLD OUT",
  redeemable: "Redeemable",
  "insufficient-funds": "Not Enough Funds",
};

export const getPrizeStatus = (
  item: StoreItem,
  currentPoints: number
): PrizeStatus => {
  const isSoldOut = item.is_sold_out || item.quantity_available <= 0;
  if (isSoldOut) return "sold-out";
  if (item.point_cost <= currentPoints) return "redeemable";
  return "insufficient-funds";
};

const PrizeView = ({ item, currentPoints }: Props) => {
  const status = getPrizeStatus(item, currentPoints);
  const isAvailable = status !== "sold-out";
  const pointsAway = item.point_cost - currentPoints;

  return (
    <ContentContainer>
      <ContentWrapper>
        <FlexHeading>
          <TypeContainer>
            <TitleText mods="heading h3">{item.name}</TitleText>
          </TypeContainer>
          <ChipsContainer>
            <StatusOutline status={status}>
              <StatusText mods="p" status={status}>
                {STATUS_LABELS[status]}
              </StatusText>
            </StatusOutline>
            <BalanceChip isComplete={!isAvailable} points={item.point_cost} />
          </ChipsContainer>
          {status === "insufficient-funds" &&
            currentPoints >= pointsAway * 0.3 && (
              <PointsContainer>
                <PointsIcon variant="primary" />
                <AffordingText mods="p" isAvailable={isAvailable}>
                  {`${pointsAway} points away from affording one!`}
                </AffordingText>
              </PointsContainer>
            )}
        </FlexHeading>
        <ItemMedia>
          <ItemImage
            src={item.image_url ? item.image_url : EmptyImage}
            alt={`${item.name} Image`}
            isAvailable={isAvailable}
          />
        </ItemMedia>
        {item.description && (
          <SubtitleText mods="p">{item.description}</SubtitleText>
        )}
        {/* <TypeContainer>
          <PillOutline>
            <PillText mods="p">
              {`Max ${item.user_redemption_limit} per person`}
            </PillText>
          </PillOutline>
          <PillOutline>
            <PillText mods="p">{`${item.quantity_available} remaining`}</PillText>
          </PillOutline>
        </TypeContainer> */}
      </ContentWrapper>
    </ContentContainer>
  );
};

const FlexHeading = styled.div`
  min-width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StatusOutline = styled.div<{ status: PrizeStatus }>`
  background: ${({ theme, status }) => {
    switch (status) {
      case "redeemable":
        return theme.globalConstants.color.backgroundSuccessLight;
      case "insufficient-funds":
        return theme.globalConstants.color.backgroundWarningLight;
      case "sold-out":
        return theme.globalConstants.color.backgroundDangerLight;
    }
  }};
  display: flex;
  padding: 4px 9.189px;
  align-items: center;
  gap: 4px;
  border-radius: 32px;
  height: 32px;
  min-width: 0;
  overflow: hidden;
`;

const AffordingText = styled(Text)<{ isAvailable: boolean }>`
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary} !important;
  line-height: normal !important;
  overflow: hidden;
  display: -webkit-box !important; /* stylelint-disable-line value-no-vendor-prefix */
  -webkit-box-orient: vertical; /* stylelint-disable-line property-no-vendor-prefix */
  line-clamp: 2;
  -webkit-line-clamp: 2; /* stylelint-disable-line property-no-vendor-prefix */
  text-overflow: ellipsis;
  opacity: ${({ isAvailable }) => (isAvailable ? 1 : 0.5)};
  font-weight: 500;

  ${mediaQueries.medium} {
    line-clamp: 4;
    -webkit-line-clamp: 4; /* stylelint-disable-line property-no-vendor-prefix */
  }
`;

const StatusText = styled(Text)<{ status: PrizeStatus }>`
  color: ${({ status }) => {
    switch (status) {
      case "redeemable":
        return "#54C152 !important";
      case "insufficient-funds":
        return "#D97706 !important";
      case "sold-out":
        return "#D95858 !important";
      default:
        return "#1F2937 !important";
    }
  }};
  font-size: 14px;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ContentContainer = styled.div`
  width: 50vw;
  max-width: 100%;
  min-width: 528px;
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 24px;

  ${mediaQueries.tablet} {
    flex-direction: column;
    min-width: 0;
    width: 100%;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
`;

const SubtitleText = styled(Text)`
  color: #6b7280 !important;
  line-height: normal !important;
  overflow: hidden;
`;

const TypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
`;

const ChipsContainer = styled(TypeContainer)`
  flex-shrink: 1;
  max-width: 100%;
  min-width: 0;

  > :last-child {
    flex-shrink: 0;
  }
`;

const PointsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-basis: 100%;
  gap: 8px;
`;

const ItemMedia = styled.div`
  position: relative;
  width: 100%;
  align-self: stretch;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};

  ${mediaQueries.tablet} {
    height: 200px;
  }
`;

const ItemImage = styled.img<{ isAvailable: boolean }>`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  opacity: ${({ isAvailable }) => (isAvailable ? 1 : 0.5)};
`;

const TitleText = styled(Text)`
  color: #374151 !important;
  line-height: normal !important;
`;

export default PrizeView;
